import { onMounted, watch, type Ref } from 'vue';
import { Modal } from 'bootstrap';

// Request modals whose hide is still running, and the shows waiting for them. Shared by every
// request modal, for the reason below.
const hiding = new Set<HTMLElement>();
const waiting = new Set<() => void>();

/**
 * A modal that is open while a store request is pending — App's choice applicator and edge picker.
 * It shows when `request` reads truthy and hides when the request is settled.
 *
 * A flow may ask again the moment it is answered (a chosen option asks for its target; an edge pick
 * follows a choice set), so the next request often arrives while a modal is still closing. Bootstrap
 * 5.3 ignores show() on a modal whose hide is running, which left the request pending behind a closed
 * modal; and a hide that ends after another modal has opened takes away the page lock (`modal-open`,
 * the scrollbar padding) that modal relies on. So a show waits until no request modal is hiding, and
 * is dropped if its request was settled in the meantime.
 */
export function useRequestModal(elRef: Ref<HTMLElement | null>, request: () => unknown) {
  let modal: Modal | null = null;
  const show = () => {
    if (request()) modal?.show();
  };

  onMounted(() => {
    const el = elRef.value;
    if (!el) return;
    modal = new Modal(el, { backdrop: 'static', keyboard: false });
    el.addEventListener('hide.bs.modal', () => hiding.add(el));
    el.addEventListener('hidden.bs.modal', () => {
      hiding.delete(el);
      if (hiding.size > 0) return;
      const shows = [...waiting];
      waiting.clear();
      shows.forEach((run) => run());
    });
  });

  watch(request, (next) => {
    if (!modal) return;
    if (!next) modal.hide();
    else if (hiding.size > 0) waiting.add(show);
    else show();
  });
}
